import React, { useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Attachment, MAX_ATTACHMENTS, fileToAttachment } from '../utils/imageAttachment';
import { DRAFT_QUOTE_OBJECT_BY_ID_QUERY } from '../graphql/draftQuotes';
import { FURNITURE_DRAFT_QUOTE_OBJECTS_QUERY } from '../graphql/draftQuoteObjects';
import './NewBlueprintSession.css';

const BROWSE_DEFAULT_LIMIT = 10;

interface FurnitureObjectRow {
  id: number;
  name: string | null;
  quantity: number;
  itemType: string;
  archivedAt: string | null;
  createdAt: string;
  furnitureType: string | null;
  quoteName: string | null;
  customerName: string | null;
  jobName: string | null;
  // A draft_quote_object can back line items on more than one work order
  // (work_order_line_item.draft_quote_object_id has no uniqueness
  // constraint — see CLAUDE.md's Roller Shade section), so this is every
  // one referencing this object, not just the first.
  workOrders: { id: number; travelerNumber: string | null }[];
  [key: string]: any;
}

// Blueprint generation is scoped to seating pieces only — the AI's system
// prompt, few-shot examples, and structural validation are all tuned for
// this parametric box scheme (legs/deck/arms/back) and don't generalize to
// non-seating furniture.
const FURNITURE_TYPES = ['Loveseat', 'Sofa / Couch', 'Sectional / L-Couch'];

interface SpecField {
  key: string;
  label: string;
  hint?: string;
}

const UNIVERSAL_SPECS: SpecField[] = [
  { key: 'width', label: 'Width', hint: 'left to right' },
  { key: 'depth', label: 'Depth', hint: 'front to back' },
  { key: 'height', label: 'Height', hint: 'floor to top' },
];

const SEATING_SPECS: SpecField[] = [
  { key: 'seatHeight', label: 'Seat Height', hint: 'floor to top of cushion' },
  { key: 'seatDepth', label: 'Seat Depth', hint: 'front edge to back of seat' },
  { key: 'armHeight', label: 'Arm Height', hint: 'floor to top of armrest' },
  { key: 'backHeight', label: 'Back Height', hint: 'top of seat to top of back' },
];

const EXTRA_SPECS: SpecField[] = [
  { key: 'legHeight', label: 'Leg Height' },
  { key: 'cushionThickness', label: 'Cushion Thickness' },
];

function buildPrompt(
  furnitureType: string,
  specs: Record<string, string>,
  notes: string
): string {
  const lines: string[] = [`Design a ${furnitureType.toLowerCase()}.`];

  const specLines = Object.entries(specs)
    .filter(([, v]) => v.trim() !== '')
    .map(([key, v]) => {
      const all = [...UNIVERSAL_SPECS, ...SEATING_SPECS, ...EXTRA_SPECS];
      const field = all.find((f) => f.key === key);
      return `${field?.label || key}: ${v}in`;
    });

  if (specLines.length) {
    lines.push(`Specs — ${specLines.join(', ')}.`);
  }

  if (notes.trim()) {
    lines.push(notes.trim());
  }

  return lines.join('\n');
}

// Fills the form with the loveseat measurements used to build/validate the
// AI's spec-driven few-shot example (see AI_BLUEPRINT_GUIDE.md's "Few-shot
// examples" section) — a one-click way to re-run that exact test case
// instead of retyping 9 fields by hand on every iteration.
const TEST_LOVESEAT_SPECS: Record<string, string> = {
  width: '63',
  depth: '33',
  height: '32',
  seatHeight: '18',
  seatDepth: '22',
  armHeight: '26',
  backHeight: '15',
  legHeight: '7',
  cushionThickness: '4',
};
const TEST_LOVESEAT_NOTES =
  'Leg Profile: 1x1in square pegs. Arm Thickness: 4in. Seat Deck Thickness: 7in. Back Cushion Thickness: 6in.';

// Fills the form with the sofa/couch measurements used to build/validate the
// AI's sofa-specific few-shot example (see blueprintFewShot.ts's
// SOFA_GENERATE_PROMPT/SOFA_BLUEPRINT) — a one-click way to re-run that
// exact test case instead of retyping 9 fields by hand on every iteration.
const TEST_SOFA_SPECS: Record<string, string> = {
  width: '96',
  depth: '36',
  height: '39',
  seatHeight: '19',
  seatDepth: '23',
  armHeight: '29',
  backHeight: '18',
  legHeight: '4.5',
  cushionThickness: '6',
};
const TEST_SOFA_NOTES = 'The back cushions rest on the seat cushions.';

// Maps draft_quote_object.furniture_type (migration 082) to this page's
// FURNITURE_TYPES labels — set on the draft object via FurnitureForm.tsx's
// "Furniture Style" field, using the same 3 slugs.
const FURNITURE_TYPE_SLUG_TO_LABEL: Record<string, string> = {
  loveseat: 'Loveseat', sofa_couch: 'Sofa / Couch', sectional: 'Sectional / L-Couch',
};

// Maps a draft_quote_object's own field names to this form's spec keys —
// only the ones this form actually has inputs for (see UNIVERSAL_SPECS/
// SEATING_SPECS/EXTRA_SPECS above). Anything else on the object (leg
// profile, arm/seat-deck/back-cushion thickness, fabric) has no matching
// spec field and is folded into Additional Notes instead.
const DRAFT_OBJECT_SPEC_KEYS: [string, string][] = [
  ['widthInches', 'width'],
  ['depthInches', 'depth'],
  ['heightInches', 'height'],
  ['seatHeightInches', 'seatHeight'],
  ['seatDepthInches', 'seatDepth'],
  ['armHeightInches', 'armHeight'],
  ['backHeightInches', 'backHeight'],
  ['legHeightInches', 'legHeight'],
  ['cushionThicknessInches', 'cushionThickness'],
];

export default function NewBlueprintSession() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftQuoteObjectId = searchParams.get('draftQuoteObjectId');
  const [furnitureType, setFurnitureType] = useState('');
  const [specs, setSpecs] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState('');
  const [showTypeError, setShowTypeError] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [prefillBanner, setPrefillBanner] = useState<string | null>(null);
  // Carried through to /blueprints/session so its "save & add to work
  // order" buttons know which draft_quote_object to attach saved images to.
  // Only ever set from a successful prefill (furniture-type objects only —
  // see applyDraftObjectPrefill's early-return), never typed in directly.
  const [selectedDraftQuoteObjectId, setSelectedDraftQuoteObjectId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [furnitureObjects, setFurnitureObjects] = useState<FurnitureObjectRow[]>([]);
  const [browseSearch, setBrowseSearch] = useState('');
  const [browseCustomer, setBrowseCustomer] = useState('');
  const [browseJob, setBrowseJob] = useState('');

  useQuery(FURNITURE_DRAFT_QUOTE_OBJECTS_QUERY, {
    onCompleted: (data) => {
      const rows: FurnitureObjectRow[] = (data?.allDraftQuoteObjects?.nodes || [])
        .filter((n: any) => !n.archivedAt)
        .map((n: any) => {
          const seenIds = new Set<number>();
          const workOrders: { id: number; travelerNumber: string | null }[] = (n.workOrderLineItemsByDraftQuoteObjectId?.nodes || [])
            .map((li: any) => li.workOrderByWorkOrderId)
            .filter((wo: any) => wo != null && !seenIds.has(wo.id) && seenIds.add(wo.id));
          return {
            ...n,
            quoteName: n.draftQuoteByDraftQuoteId?.name || null,
            customerName: n.draftQuoteByDraftQuoteId?.jobByJobId?.customerByCustomerId?.name || null,
            jobName: n.draftQuoteByDraftQuoteId?.jobByJobId?.name || null,
            workOrders,
          };
        });
      setFurnitureObjects(rows);
    },
  });

  // Distinct, sorted customer names present in the fetched batch — drives
  // the Customer filter's options. Recomputed on every render rather than
  // memoized (this list is at most 200 rows).
  function getBrowseCustomerOptions(): string[] {
    const seen = new Set<string>();
    for (const r of furnitureObjects) {
      if (r.customerName) seen.add(r.customerName);
    }
    return Array.from(seen).sort();
  }

  // Job options narrow to whatever customer is currently selected, so
  // picking a customer first doesn't leave stale/impossible jobs selectable.
  function getBrowseJobOptions(): string[] {
    const seen = new Set<string>();
    for (const r of furnitureObjects) {
      if (browseCustomer && r.customerName !== browseCustomer) continue;
      if (r.jobName) seen.add(r.jobName);
    }
    return Array.from(seen).sort();
  }

  function getBrowseRows(): FurnitureObjectRow[] {
    const q = browseSearch.trim().toLowerCase();
    const isFiltering = !!(q || browseCustomer || browseJob);
    let rows = furnitureObjects;
    if (browseCustomer) rows = rows.filter((r) => r.customerName === browseCustomer);
    if (browseJob) rows = rows.filter((r) => r.jobName === browseJob);
    if (q) rows = rows.filter((r) => (r.name || '').toLowerCase().includes(q));
    // Rows already arrive newest-first (orderBy: CREATED_AT_DESC), so with no
    // active search/filter this is simply the 10 most recent.
    return isFiltering ? rows : rows.slice(0, BROWSE_DEFAULT_LIMIT);
  }

  function handleBrowseRowClick(row: FurnitureObjectRow) {
    applyDraftObjectPrefill(row);
  }

  useQuery(DRAFT_QUOTE_OBJECT_BY_ID_QUERY, {
    variables: { id: parseInt(draftQuoteObjectId || '0', 10) },
    skip: !draftQuoteObjectId,
    onCompleted: (data) => applyDraftObjectPrefill(data?.draftQuoteObjectById),
    onError: () => setPrefillBanner(`Couldn't load draft object #${draftQuoteObjectId} — it may have been deleted.`),
  });

  // Fulfills the ?draftQuoteObjectId=... deep link from a Work Order's
  // furniture line item (WorkOrderDetail.tsx's "Design in Blueprints" link):
  // maps the object's furniture_type (migration 082) and every known
  // measurement onto this form's fields, and folds anything this form has no
  // dedicated field for (leg profile, arm/seat-deck/back-cushion thickness,
  // fabric) into Additional Notes instead of dropping it.
  function applyDraftObjectPrefill(obj: any) {
    if (!obj) {
      setPrefillBanner(`Couldn't find draft object #${draftQuoteObjectId}.`);
      return;
    }
    if (String(obj.itemType).toLowerCase() !== 'furniture') {
      setPrefillBanner(`Draft object #${obj.id} isn't a furniture item (type: ${obj.itemType}) — nothing was prefilled.`);
      return;
    }

    setSelectedDraftQuoteObjectId(Number(obj.id));

    const label = obj.furnitureType ? FURNITURE_TYPE_SLUG_TO_LABEL[obj.furnitureType] : null;
    if (label) setFurnitureType(label);

    const nextSpecs: Record<string, string> = {};
    DRAFT_OBJECT_SPEC_KEYS.forEach(([objKey, specKey]) => {
      const v = obj[objKey];
      if (v != null) nextSpecs[specKey] = String(Number(v));
    });
    setSpecs(nextSpecs);

    const extraLines: string[] = [];
    if (obj.legProfile) extraLines.push(`Leg Profile: ${obj.legProfile}.`);
    if (obj.armThicknessInches != null) extraLines.push(`Arm Thickness: ${Number(obj.armThicknessInches)}in.`);
    if (obj.seatDeckThicknessInches != null) extraLines.push(`Seat Deck Thickness: ${Number(obj.seatDeckThicknessInches)}in.`);
    if (obj.backCushionThicknessInches != null) extraLines.push(`Back Cushion Thickness: ${Number(obj.backCushionThicknessInches)}in.`);
    if (obj.fabricDescription) extraLines.push(`Fabric: ${obj.fabricDescription}.`);
    setNotes(extraLines.join(' '));

    const source = obj.name ? `"${obj.name}"` : `draft object #${obj.id}`;
    const qtyNote = Number(obj.quantity) > 1 ? ` (×${obj.quantity})` : '';
    const typeNote = label ? '' : ' No furniture style was set on this object — please pick one below.';
    setPrefillBanner(`Prefilled from ${source}${qtyNote}.${typeNote}`);
  }

  function handleAutofillTestLoveseat() {
    setFurnitureType('Loveseat');
    setSpecs(TEST_LOVESEAT_SPECS);
    setNotes(TEST_LOVESEAT_NOTES);
    setShowTypeError(false);
  }

  function handleAutofillTestSofa() {
    setFurnitureType('Sofa / Couch');
    setSpecs(TEST_SOFA_SPECS);
    setNotes(TEST_SOFA_NOTES);
    setShowTypeError(false);
  }

  async function handleFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const room = MAX_ATTACHMENTS - attachments.length;
    const toAdd = files.slice(0, Math.max(0, room));
    const converted = await Promise.all(toAdd.map(fileToAttachment));
    setAttachments((prev) => [...prev, ...converted].slice(0, MAX_ATTACHMENTS));
  }

  function removeAttachment(index: number) {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }

  const visibleSpecs = [...UNIVERSAL_SPECS, ...SEATING_SPECS, ...EXTRA_SPECS];

  function updateSpec(key: string, value: string) {
    setSpecs((prev) => ({ ...prev, [key]: value }));
  }

  function handleStart() {
    if (!furnitureType) {
      setShowTypeError(true);
      return;
    }
    const prompt = buildPrompt(furnitureType, specs, notes);
    navigate('/blueprints/session', {
      state: {
        prompt,
        images: attachments.map((a) => a.dataUrl),
        draftQuoteObjectId: selectedDraftQuoteObjectId,
      },
    });
  }

  return (
    <div className="nbs-page">
      <div className="page-header">
        <div>
          <h1>New Blueprint</h1>
          <p className="subtitle">
            Pick a furniture type and, optionally, the dimensions you want the AI to honor —
            it'll fall back to standard proportions for anything left blank.
          </p>
        </div>
        <div className="nbs-autofill-group">
          <button
            type="button"
            className="nbs-autofill-btn"
            onClick={handleAutofillTestLoveseat}
            title="Fills the form with the loveseat measurements used to build the AI's spec-driven few-shot example"
          >
            Autofill Test Loveseat
          </button>
          <button
            type="button"
            className="nbs-autofill-btn"
            onClick={handleAutofillTestSofa}
            title="Fills the form with the sofa/couch measurements used to build the AI's sofa-specific few-shot example"
          >
            Autofill Test Sofa
          </button>
        </div>
      </div>

      <div className="page-content">
        <div className="nbs-browse-panel">
          <div className="nbs-browse-title">Browse furniture objects</div>
          <div className="nbs-browse-controls">
            <input
              type="text"
              className="nbs-select nbs-browse-search"
              placeholder="Search object name…"
              value={browseSearch}
              onChange={(e) => setBrowseSearch(e.target.value)}
            />
            <select
              className="nbs-select"
              value={browseCustomer}
              onChange={(e) => {
                setBrowseCustomer(e.target.value);
                setBrowseJob('');
              }}
            >
              <option value="">All customers</option>
              {getBrowseCustomerOptions().map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <select
              className="nbs-select"
              value={browseJob}
              onChange={(e) => setBrowseJob(e.target.value)}
            >
              <option value="">All jobs</option>
              {getBrowseJobOptions().map((j) => (
                <option key={j} value={j}>{j}</option>
              ))}
            </select>
          </div>

          <div className="nbs-browse-table-wrap">
            <table className="nbs-browse-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Quote</th>
                  <th>Customer</th>
                  <th>Job</th>
                  <th>Work Order</th>
                </tr>
              </thead>
              <tbody>
                {getBrowseRows().map((row) => (
                  <tr key={row.id} onClick={() => handleBrowseRowClick(row)} title="Click to prefill the form">
                    <td>{row.id}</td>
                    <td>{row.name || ''}</td>
                    <td>{row.quoteName || ''}</td>
                    <td>{row.customerName || ''}</td>
                    <td>{row.jobName || ''}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {row.workOrders.map((wo, i) => (
                        <React.Fragment key={wo.id}>
                          {i > 0 && ', '}
                          <Link to={`/work-orders/${wo.id}`} className="nbs-browse-wo-link">
                            #{wo.id}{wo.travelerNumber ? ` (${wo.travelerNumber})` : ''}
                          </Link>
                        </React.Fragment>
                      ))}
                    </td>
                  </tr>
                ))}
                {getBrowseRows().length === 0 && (
                  <tr className="nbs-browse-empty-row">
                    <td colSpan={6}>No furniture objects found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="nbs-card">
          {prefillBanner && (
            <div className="nbs-field" style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#3730a3' }}>
              {prefillBanner}
            </div>
          )}

          <div className="nbs-field-row">
            <div className="nbs-field nbs-field-type">
              <label htmlFor="nbs-furniture-type" className="nbs-label">
                Furniture Type <span className="nbs-required">*</span>
              </label>
              <select
                id="nbs-furniture-type"
                className={`nbs-select${showTypeError ? ' nbs-input-error' : ''}`}
                value={furnitureType}
                onChange={(e) => {
                  setFurnitureType(e.target.value);
                  if (e.target.value) setShowTypeError(false);
                }}
              >
                <option value="">Select a type…</option>
                {FURNITURE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              {showTypeError && (
                <div className="nbs-error-text">Choose a furniture type to continue.</div>
              )}
            </div>

            <div className="nbs-field nbs-field-images">
              <div className="nbs-label">
                Reference Images <span className="nbs-optional">(optional, up to {MAX_ATTACHMENTS})</span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handleFilesSelected}
              />
              <div className="nbs-attachments">
                {attachments.map((a, i) => (
                  <div key={i} className="nbs-attachment">
                    <img src={a.dataUrl} alt={a.name} />
                    <button
                      type="button"
                      className="nbs-attachment-remove"
                      aria-label={`Remove ${a.name}`}
                      onClick={() => removeAttachment(i)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {attachments.length < MAX_ATTACHMENTS && (
                  <button
                    type="button"
                    className="nbs-attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    + Add
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="nbs-field">
            <div className="nbs-label">Specs <span className="nbs-optional">(optional)</span></div>
            <div className="nbs-specs-grid">
              {visibleSpecs.map((f) => (
                <div key={f.key} className="nbs-spec">
                  <label htmlFor={`nbs-spec-${f.key}`} className="nbs-spec-label">
                    {f.label}
                    {f.hint && <span className="nbs-spec-hint"> — {f.hint}</span>}
                  </label>
                  <div className="nbs-spec-input-wrap">
                    <input
                      id={`nbs-spec-${f.key}`}
                      type="number"
                      min={0}
                      step="0.25"
                      className="nbs-spec-input"
                      placeholder="—"
                      value={specs[f.key] || ''}
                      onChange={(e) => updateSpec(f.key, e.target.value)}
                    />
                    <span className="nbs-spec-unit">in</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="nbs-field">
            <label htmlFor="nbs-notes" className="nbs-label">
              Additional Notes <span className="nbs-optional">(optional)</span>
            </label>
            <textarea
              id="nbs-notes"
              className="nbs-notes"
              rows={4}
              placeholder="Style cues, materials, special requests…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="nbs-actions">
            <button type="button" className="primary-button" onClick={handleStart}>
              Start Session
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
